import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Compass, Hammer, Settings, ArrowRightLeft,
  Brain, Briefcase, Users, ShieldCheck,
  Building2, GraduationCap, BookOpen, Cpu,
  Rocket, Lightbulb, Target, ShieldAlert, Repeat, TrendingUp,
  Layers3, PackagePlus, Wallet, ArrowRight, CheckCircle2
} from "lucide-react";
import "./GCCServices.css";

const bot = [
  { icon: Compass, title: "Plan", text: "Lay the foundation of a future-ready with innovative GCC.", tab: "plan" },
  { icon: Hammer, title: "Build", text: "Start Building your new age, AI driven R&D and Services team using our ecosystem.", tab: "build" },
  { icon: Settings, title: "Operate", text: "Operate your GCC with confidence and be empowered to thrive. Drive organizational transformation.", tab: "operate" },
  { icon: ArrowRightLeft, title: "Transfer", text: "Unlock a smooth handover strategy with change management and end-to-end transfer.", tab: "transfer" },
];

const ecosystem = [
  { icon: Brain, title: "Global AI Studios", text: "Continuous innovation through cutting-edge AI solutions tailored to your needs." },
  { icon: Briefcase, title: "Deep Industry Expertise", text: "Aligning solutions with your business goals for maximum impact." },
  { icon: Users, title: "Trusted Partner Network", text: "Fast-track value realization with our reliable ecosystem of partners." },
  { icon: ShieldCheck, title: "Low-Risk GCC Model", text: "Build your own GCC with a less risky, scalable approach." },
];

const offshore = [
  { icon: Building2, title: "Smart Infrastructure", text: "Scalable office infrastructure designed for efficiency and growth." },
  { icon: GraduationCap, title: "Future-Skilled Talent", text: "Access to high-caliber professionals with cutting-edge skills." },
  { icon: BookOpen, title: "Knowledge Management", text: "Strong practices to ensure seamless knowledge transfer and retention." },
  { icon: Cpu, title: "AI-Infused Operations", text: "Boost efficiency and impact with AI-driven processes." },
];

const keyPoints = [
  { icon: Rocket, title: "Reduced time to market" },
  { icon: Lightbulb, title: "Faster Innovation" },
  { icon: Target, title: "Absolute control on outcome. More involved" },
  { icon: ShieldAlert, title: "Reduced risk of setting up entities abroad" },
  { icon: Repeat, title: "Flexible to acquire when ready" },
  { icon: TrendingUp, title: "Easy to scale with gradual approach" },
  { icon: Layers3, title: "Minimize the effort before acquisition" },
  { icon: PackagePlus, title: "Access to additional services" },
  { icon: Wallet, title: "No overhead cost when the team is small" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const GCCServices = () => {
  return (
    <div className="gcc-page">
      {/* Hero */}
      <section className="gcc-hero">
        <div className="gcc-hero__blob gcc-hero__blob--a" />
        <div className="gcc-hero__blob gcc-hero__blob--b" />
        <div className="gcc-hero__grid" />
        <motion.div
          className="gcc-hero__inner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="gcc-hero__badge">
            <Sparkles size={14} /> AI Enabled GCC Services
          </span>
          <h1 className="gcc-hero__title">
            Your Own <span>Offshore Company</span><br />with Reduced Risk
          </h1>
          <p className="gcc-hero__lead gcc-hero__lead--bold">Reimagine Your Global Capability Center</p>
          <p className="gcc-hero__sub">From Operational Backbone to Innovation Powerhouse.</p>
          <p className="gcc-hero__para">
            Today's enterprises need scalable innovation, AI-led transformation, and speed.
            That's where we come in.
          </p>
          <p className="gcc-hero__para">
            We go beyond traditional GCC setup. We build and scale future-ready Global Capability
            Centers designed to be high-performance engines of growth.
          </p>
        </motion.div>
      </section>

      {/* BOT Model */}
      <section className="gcc-section container">
        <motion.div
          className="gcc-head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>What is SpringUp's <span>BOT</span> based delivery model?</h2>
          <p>Build · Operate · Transfer — a proven four-phase journey to launch your future-ready GCC.</p>
        </motion.div>
        <div className="gcc-bot">
          <div className="gcc-bot__line" />
          {bot.map((b, i) => (
            <motion.div
              key={b.title}
              className="gcc-bot__step"
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="gcc-bot__node">
                <span className="gcc-bot__index">0{i + 1}</span>
                <div className="gcc-bot__icon"><b.icon size={26} /></div>
              </div>
              <div className="gcc-bot__card">
                <h3>{b.title}</h3>
                <p>{b.text}</p>
                <Link to={`/gcc-build-services?tab=${b.tab}`} className="gcc-bot__more">
                  Read more <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section className="gcc-section gcc-section--soft">
        <motion.div
          className="gcc-head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>Our Ecosystem Approach <span>Integrates</span></h2>
        </motion.div>
        <div className="gcc-grid4">
          {ecosystem.map((e, i) => (
            <motion.div
              key={e.title}
              className="gcc-ecard"
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="gcc-ecard__icon"><e.icon size={24} /></div>
              <h4>{e.title}</h4>
              <p>{e.text}</p>
              <span className="gcc-ecard__corner" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Offshore */}
      <section className="gcc-section">
        <motion.div
          className="gcc-head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>Dedicated <span>Offshore Office</span> Model</h2>
          <p>With our AI-infused model, you gain</p>
        </motion.div>
        <div className="gcc-grid4">
          {offshore.map((o, i) => (
            <motion.div
              key={o.title}
              className="gcc-ocard"
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="gcc-ocard__ring"><o.icon size={26} /></div>
              <h4>{o.title}</h4>
              <p>{o.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Points */}
      <section className="gcc-section gcc-section--gradient">
        <motion.div
          className="gcc-head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2>Accelerate Your Success: <span>Key Points</span></h2>
          <p>
            Power your GCC with next-gen platforms that accelerate hiring, harmonize operations,
            personalize learning, and automate workflows—driving smarter, faster, and more efficient
            global delivery.
          </p>
        </motion.div>
        <div className="gcc-kp">
          {keyPoints.map((k, i) => (
            <motion.div
              key={k.title}
              className="gcc-kp__item"
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <span className="gcc-kp__num">{String(i + 1).padStart(2, "0")}</span>
              <div className="gcc-kp__icon"><k.icon size={22} /></div>
              <h5>{k.title}</h5>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Case Study 1 - DNV */}
      <section className="gcc-section">
        <motion.div
          className="gcc-head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <span className="gcc-tag">Case Study 1</span>
          <h2>DNV: <span>Hybrid Model</span></h2>
        </motion.div>
        <div className="gcc-dnv">
          <motion.div
            className="gcc-dnv__intro"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="gcc-dnv__stat"><h4>Norwegian</h4><p>Conglomerate</p></div>
            <div className="gcc-dnv__stat"><h4>Multi-Domain</h4><p>Products across industry domains</p></div>
            <div className="gcc-dnv__stat">
              <h4>Operational DNV India</h4>
              <p> 40 Developers and 100+ Piping Engineering Talent in Pune</p>
            </div>
          </motion.div>

          {[
            {
              title: "Ship Calibration and Certification Division",
              points: [
                "Project/Product Management from Norway",
                "UI/UX from Norway",
                "Product Owner, Tech Architect, Developers and Testing team from SpringUp",
                "Operational alignment: Laptop, IT, Finance, VDI, Onboarding with DNV India",
                "Client Interviews",
              ],
            },
            {
              title: "Pipeline and Hydraulic Division - US",
              points: [
                "Work with DNV India team. Tasks are divided for every SPRINT",
                "SpringUp team works independently and reports",
                "SpringUp Lab has Developers, Testers",
                "Operational alignment: Laptop, IT, Finance, VDI, Onboarding with DNV India",
                "Client Interviews",
              ],
            },
            {
              title: "Independent Quality",
              points: [
                "Independent team, part of the SPRINT and directly reports to Norway",
                "Aligned directly with Product Owner",
                "Operational alignment: Laptop, IT, Finance, VDI, Onboarding with DNV India",
                "Client Interviews",
              ],
            },
          ].map((d, i) => (
            <motion.div
              key={d.title}
              className="gcc-dnv__card"
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="gcc-dnv__cardhead">
                <span className="gcc-dnv__badge">Operating Model</span>
                <h3>{d.title}</h3>
              </div>
              <ul className="gcc-dnv__list">
                {d.points.map((p, idx) => (
                  <li key={idx}><CheckCircle2 size={16} /><span>{p}</span></li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Case Study 2 - BOT */}
      <section className="gcc-section  container">
        <motion.div className="gcc-head" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <span className="gcc-tag">Case Study 2</span>
          <h2>25 People <span>BOT Team</span></h2>
        </motion.div>

        {/* Client info banner */}
        <motion.div className="gcc-cs2b"
          initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <div className="gcc-cs2b__logo">
            <img src="https://springuplabs.com/asset/img/logoheinekingmedia.png" alt="Heinekingmedia logo" />
          </div>
          <div className="gcc-cs2b__info">
            <h3>Client and Engagement Highlights</h3>
            <ul>
              <li><CheckCircle2 size={16} /> Client is a German EdTech Company</li>
              <li><CheckCircle2 size={16} /> Develops Tech Products and Smart Class Room</li>
              <li><CheckCircle2 size={16} /> Operates Across 3 countries, 80 Associates</li>
            </ul>
          </div>
          <div className="gcc-cs2b__tech">
            <h3>Platform and Technology Details</h3>
            <div className="gcc-cs2__chips">
              <span>Full Stack</span><span>Web Application</span><span>Cloud</span>
            </div>
          </div>
        </motion.div>

        {/* 3 cards row */}
        <div className="gcc-cs2t">
          {[
            {
              cls: "gcc-cs2t__card--challenge",
              icon: Target,
              title: "Business Challenges",
              items: [
                "Ability to scale quickly",
                "Availability of tech talent to fuel the innovation",
                "Ability to release new products in time",
                "Technology Revamp",
                "Improve Profit margin",
              ],
            },
            {
              cls: "gcc-cs2t__card--solution",
              icon: Lightbulb,
              title: "Our Solution",
              items: [
                "Build initial team of 10 engineers, Architect, Senior Developers, Testing and UI-UX experts",
                "Product Ownership and Tech Redesign. Develop and Release the product version",
                "A joint team across two continents and worked in joint model.",
                "Communication & Governance",
              ],
            },
            {
              cls: "gcc-cs2t__card--outcome",
              icon: TrendingUp,
              title: "Outcomes",
              items: [
                "Reduce the cost by 43%",
                "Improved customer support. 33% Reduced case",
                "Products released on time",
              ],
            },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              className={`gcc-cs2t__card ${c.cls}`}
              custom={i}
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <div className="gcc-cs2t__bar" />
              <div className="gcc-cs2t__head">
                <div className="gcc-cs2t__icon"><c.icon size={22} /></div>
                <h4>{c.title}</h4>
              </div>
              <ul>
                {c.items.map((it) => (
                  <li key={it}>{it}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GCCServices;